// The user's Autodesk (ACC) connection: connected as <email> / not connected, with
// Connect (the connect window: browser connector, or the Autodesk sign-in) and Disconnect. The state is
// checked against Autodesk by the server on each load, so a revoked grant shows here.
// `compact` is the personal-area strip; the profile tab uses the full panel.
import React, { useCallback, useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import addNotification from "helpers/notify";
import { getAccStatus, disconnectAcc } from "./accApi";
import { openAccConnect } from "./AccConnectModal";
import { AutodeskLogo } from "components/Integrations/ProviderLogos";
import { refreshProviderStatus } from "components/Integrations/useIntegrations";
import AccountsCount from "components/Integrations/AccountsCount";
import "./acc.scss";

export function useAccStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await getAccStatus());
    } catch (e) {
      setStatus({ configured: true, connected: false, error: e.message });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  return { status, loading, reload: load };
}

// THE CONNECT WINDOW (AccConnectModal): the browser connector first, the Autodesk sign-in
// as the extra way, like Procore's. { connection: 'new' } connects an ADDITIONAL Autodesk
// account (owner 2026-10-24); an id reconnects that one. Called from onClick too, so a
// click event is not a connection.
export function connectAcc(opts) {
  const connection = opts && typeof opts === "object" && typeof opts.connection === "string" ? opts.connection : "";
  openAccConnect(null, { connection });
}

export default function AccConnectionPanel({ compact = false }) {
  const { status, loading, reload } = useAccStatus();

  // back from the Autodesk sign-in: /personal-area?acc=connected|cancelled|expired
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("acc");
    if (!q) return;
    if (q === "connected") addNotification("Autodesk account connected", "success");
    else if (q === "cancelled") addNotification("Autodesk sign-in was cancelled", "warning");
    else if (q === "expired") addNotification("The Autodesk sign-in expired - connect again", "warning");
    const u = new URL(window.location.href);
    u.searchParams.delete("acc");
    window.history.replaceState({}, "", u.pathname + u.search + u.hash);
  }, []);

  const disconnect = () =>
    Modal.confirm({
      title: "Disconnect the Autodesk account?",
      content: "Linked projects stay linked, but they stop updating from ACC until an account is connected again.",
      okText: "Disconnect",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await disconnectAcc();
          addNotification("Autodesk account disconnected", "success");
        } catch (e) {
          addNotification(e.message, "danger");
        }
        reload();
        refreshProviderStatus("acc");
      },
    });

  if (loading && !status) return compact ? null : <Spin />;
  // NOT FOR THIS READER (owner 2026-10-19). Connecting ACC belongs to a
  // project owner, a company admin or an admin, because they hold the
  // permission and set the automatic folder delivery. The server answers
  // `allowed: false` on /acc/status; drawing nothing is the point - a
  // Connect button that 403s on click reads as a broken feature. The full
  // panel says WHO can, so the reader knows who to ask rather than
  // wondering where it went.
  if (status && status.allowed === false) {
    return compact ? null : <p className="text-muted">{status.reason || "Connecting Autodesk (ACC) is limited to a project owner, a company admin, an admin, or someone granted the accConnect permission."}</p>;
  }
  if (status && status.configured === false) {
    return compact ? null : <p className="text-muted">Autodesk Construction Cloud is not set up on this server yet.</p>;
  }
  // the first account drives Connect / Disconnect; the state says whether ANY of my
  // Autodesk accounts works, and the count lists them all (owner 2026-10-25)
  const on = !!(status && status.connected);
  const anyOn = on || !!(status && (status.accounts || []).some((a) => a.connected));

  const body = (
    <div className={`acc-connection ${compact ? "compact" : ""}`}>
      <AutodeskLogo connected={anyOn} size={compact ? 20 : 28} />
      <div className="acc-connection-text">
        <div>
          Autodesk Construction Cloud: <span className={`acc-state ${anyOn ? "on" : "off"}`}>{anyOn ? "Connected" : "Not connected"}</span>
          {" · "}
          <AccountsCount list={status && status.accounts} name="ACC" testId="header-accounts-acc" />
        </div>
        {on && status.email ? (
          <small className="text-muted">
            as {status.email}
            {status.method === "browser" ? " · through the browser connector" : ""}
          </small>
        ) : null}
        {!on && status && status.error ? <small className="text-danger d-block">{status.error}</small> : null}
      </div>
      {on ? (
        <button className="bg-white text-bid border-bid shadow-none px-3" onClick={disconnect}>
          Disconnect
        </button>
      ) : (
        <button className="header-btn text-white bg-bid border-bid px-3" onClick={connectAcc}>
          Connect ACC
        </button>
      )}
    </div>
  );

  if (compact) return body;
  return (
    <div className="w-100">
      <div className="py-4 px-2">
        <p className="font-weight-bold text-black mb-1 h3">Autodesk (ACC)</p>
        <p>
          Connect your company&apos;s Autodesk account - once, for everyone in the company - to link projects to Revit models in Autodesk Construction Cloud. A linked
          project is read and published on BidLight&apos;s servers - no Revit needed - and updates when a newer
          version is saved to ACC. The settings icon beside the Autodesk logo on each project card chooses the ACC
          folders its approved shop drawings, drawings and models are delivered to.
        </p>
        <hr />
        <div className="plan-container mt-4">
          <div className="plan-content">{body}</div>
        </div>
      </div>
    </div>
  );
}
